import { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { useIsMobile } from '../hooks/useIsMobile';

const today = () => new Date().toISOString().slice(0, 7);

export default function UploadPage() {
  const isMobile = useIsMobile();
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [negateAmounts, setNegateAmounts] = useState(false);
  const [billingMonth, setBillingMonth] = useState(today());
  const [useBillingMonth, setUseBillingMonth] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const t = (() => { try { return localStorage.getItem('token'); } catch { return sessionStorage.getItem('token'); } })();
      const res = await fetch('/api/transactions/upload-history', {
        headers: { Authorization: `Bearer ${t}` }
      });
      if (res.ok) setHistory(await res.json());
    } catch (e) {}
    finally { setLoadingHistory(false); }
  };

  useEffect(() => { loadHistory(); }, []);

  const onDrop = useCallback(async (files) => {
    const file = files[0];
    if (!file) return;
    setUploading(true);
    setResult(null);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('negate_amounts', negateAmounts ? 'true' : 'false');
    if (useBillingMonth && billingMonth) {
      const [yr, mo] = billingMonth.split('-');
      formData.append('billing_year', yr);
      formData.append('billing_month', mo);
    }
    try {
      const res = await api.uploadCSV(formData);
      setResult(res);
      toast.success(`Imported ${res.created} transactions!`);
      loadHistory();
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [negateAmounts, billingMonth, useBillingMonth]);

  const deleteUpload = async (sourceFile) => {
    if (!window.confirm(`Delete ALL transactions from "${sourceFile}"? This cannot be undone.`)) return;
    try {
      const t = (() => { try { return localStorage.getItem('token'); } catch { return sessionStorage.getItem('token'); } })();
      const res = await fetch(`/api/transactions/upload-history/${encodeURIComponent(sourceFile)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${t}` }
      });
      const data = await res.json();
      toast.success(`Deleted ${data.deleted} transactions from "${sourceFile}"`);
      loadHistory();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: false,
  });

  return (
    <div className="animate-in" style={{ maxWidth: 680 }}>
      <div className="page-title">Upload Statement</div>
      <div className="page-subtitle">Drop a CSV or Excel file from your bank or credit card</div>

      <div className="card mb-4" style={{ padding: '14px 18px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--mid)', marginBottom: 12 }}>Upload Options</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', flex: '1 1 200px' }}>
            <input type="checkbox" checked={negateAmounts} onChange={e => setNegateAmounts(e.target.checked)} style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>Flip amount signs</div>
              <div style={{ fontSize: 11.5, color: 'var(--mid)', marginTop: 2 }}>Use when your bank exports expenses as positive numbers (common with credit card statements)</div>
            </div>
          </label>
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 8 }}>
              <input type="checkbox" checked={useBillingMonth} onChange={e => setUseBillingMonth(e.target.checked)} style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Assign billing month</div>
                <div style={{ fontSize: 11.5, color: 'var(--mid)', marginTop: 2 }}>Override transaction dates — useful for credit card statements</div>
              </div>
            </label>
            {useBillingMonth && (
              <input type="month" className="input" value={billingMonth} onChange={e => setBillingMonth(e.target.value)} style={{ fontSize: 14, marginLeft: 26 }} />
            )}
          </div>
        </div>
      </div>

      <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`} style={{ marginBottom: 20 }}>
        <input {...getInputProps()} />
        <div className="dropzone-icon">{uploading ? '⏳' : '↑'}</div>
        <div className="dropzone-title">
          {uploading ? 'Processing your statement…' : isDragActive ? 'Drop it!' : 'Drag & drop your statement here'}
        </div>
        <div className="dropzone-sub">
          {uploading ? 'Categorizing with AI…' : 'CSV or XLSX · Any bank format'}
        </div>
      </div>

      {result && (
        <div className="card mb-4 animate-in">
          <div className="section-title mb-4">Import Complete</div>
          <div className="grid-3">
            <div><div className="stat-label">Imported</div><div className="stat-value green">{result.created}</div></div>
            <div><div className="stat-label">Skipped (duplicates)</div><div className="stat-value" style={{ color: 'var(--mid)' }}>{result.skipped_duplicates ?? result.skipped ?? 0}</div></div>
            <div><div className="stat-label">Total Parsed</div><div className="stat-value gold">{result.total_parsed}</div></div>
          </div>
          {(negateAmounts || useBillingMonth) && (
            <div style={{ marginTop: 14, padding: '10px 14px', background: 'var(--off)', borderRadius: 6, fontSize: 12, color: 'var(--mid)' }}>
              {negateAmounts && <span style={{ marginRight: 12 }}>✓ Amounts flipped</span>}
              {useBillingMonth && <span>✓ Assigned to {billingMonth}</span>}
            </div>
          )}
        </div>
      )}

      <div className="card mb-4">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div className="section-title" style={{ marginBottom: 0 }}>Upload History</div>
          <button className="btn btn-sm btn-ghost" onClick={loadHistory}>↻ Refresh</button>
        </div>
        {loadingHistory ? (
          <div style={{ color: 'var(--mid)', fontSize: 13, padding: '10px 0' }}>Loading…</div>
        ) : history.length === 0 ? (
          <div style={{ color: 'var(--mid)', fontSize: 13, padding: '10px 0' }}>No uploads yet.</div>
        ) : (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr auto' : '2fr 1fr 1fr 1fr auto', gap: 8, padding: '0 0 8px', borderBottom: '1px solid var(--stone)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--mid)' }}>
              <div>File</div>
              {!isMobile && <div style={{ textAlign: 'right' }}>Transactions</div>}
              {!isMobile && <div style={{ textAlign: 'right' }}>Date Range</div>}
              <div style={{ textAlign: 'right' }}>Uploaded</div>
              <div></div>
            </div>
            {history.map((h, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr auto' : '2fr 1fr 1fr 1fr auto', gap: 8, padding: '12px 0', borderBottom: i < history.length - 1 ? '1px solid var(--stone)' : 'none', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.source_file}</div>
                </div>
                {!isMobile && <div style={{ textAlign: 'right', fontSize: 13 }}>{h.count} txns</div>}
                {!isMobile && <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--mid)' }}>{h.date_range}</div>}
                <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--mid)' }}>{h.uploaded_at}</div>
                <button className="btn btn-sm btn-danger" onClick={() => deleteUpload(h.source_file)} title="Delete all transactions from this file">×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card" style={{ borderStyle: 'dashed', opacity: 0.7 }}>
        <div className="section-title">How it works</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
          {[
            ['1', 'Auto-detect columns', 'Detects Date, Description, and Amount automatically for any bank format'],
            ['2', 'Historic fuzzy matching', 'Transactions 95%+ similar to past ones are instantly auto-categorized'],
            ['3', 'Rule-based matching', 'Keyword rules from your Categories page are applied next'],
            ['4', 'Claude AI for the rest', 'Remaining transactions are sent to Claude for intelligent categorization'],
            ['5', 'Duplicate protection', 'Re-uploading the same file is safe — duplicates are automatically skipped'],
          ].map(([num, title, desc]) => (
            <div key={num} style={{ display: 'flex', gap: 14 }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--accent-glow)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{num}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{title}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
